import { useEffect, useRef } from "react";
import { fetchFastApi } from "../../api/fastapi";
import {
  analyticsKeyPrefix,
  type AnalyticsOwner,
} from "../../lib/analyticsOwner";
import { useHookCache, useHookCacheQuery } from "../../lib/hookCache";
import {
  ControlSessionStatusResponse,
  hasActiveLifecycleState,
  parseControlSessionStatusResponse,
} from "../../models/audit/controlSession";
import { SessionKind } from "./useControlSessionEnvelope";

const POLL_INTERVAL_MS = 5000;

/**
 * Smart status hook for the new Phase 2 status endpoint:
 * `GET /controls/v2/control-session/{id}/status`.
 *
 * Polls every {@link POLL_INTERVAL_MS} ONLY while at least one procedure
 * session has at least one lifecycle field that is not yet `ready` (i.e.,
 * `pending` or `processing`).
 *
 * When a per-procedure lifecycle field flips into `ready`, invalidates the
 * narrow sub-key(s) tied to the data that actually changes. The heavy
 * full-hydration `["procedure-session", psId]` key is avoided where
 * possible (it prefix-matches every sub-key and re-fetches the whole
 * `useProcedureSession` payload) — but the `documentsState` branch has to
 * invalidate it because the sample-card doc-count badge reads from
 * `matchItems` inside the heavy hydration. Transitions are rare events
 * (not per-tick), so the cost is bounded.
 *
 * Field → sub-key mapping on `*State !== "ready" -> "ready"` transitions:
 *   - `documentsState`  → `["procedure-session", psId]` (cascades to
 *                         `documents`, `source-rows`, and the heavy
 *                         `useProcedureSession` hydration — the latter is
 *                         required because the sample-card doc-count
 *                         badge now reads its source of truth from
 *                         `auditSession.sourceRows[i].matchItems`, which
 *                         lives in the heavy key. Narrower invalidation
 *                         leaves the badge stale until the next manual
 *                         refresh.)
 *                         + the analytics owner's `analyticsKeyPrefix` (once
 *                         per tick, skipped when `analyticsOwner` is absent)
 *   - `sourceRowsState` → `["procedure-session", psId, "source-rows"]`
 *                         + `["procedure-session", psId, "table-shell"]`
 *   - `testsState`      → `["procedure-session", psId, "source-rows"]`
 *                         + `["procedure-session", psId, "table-shell"]`
 *                         + `["control-session", id]` (once per tick — test
 *                         runs persist the auditor trail, so the envelope's
 *                         `auditorTrailEnabled`/`auditorTrailPending` must
 *                         refresh without a page reload)
 *   - `workPaperState`  → `["procedure-session", psId, "table-shell"]`
 *   - envelope `state`  → `["procedure-session", psId, "table-shell"]`
 */
export function useControlSessionStatus(
  controlSessionId: string | undefined,
  kind: SessionKind = "control",
  analyticsOwner?: AnalyticsOwner,
) {
  const { invalidate } = useHookCache();
  const analyticsOwnerKind = analyticsOwner?.kind;
  const analyticsOwnerId = analyticsOwner?.id;
  const previousRef = useRef<ControlSessionStatusResponse | undefined>(
    undefined,
  );

  const query = useHookCacheQuery<ControlSessionStatusResponse>({
    // The key stays kind-agnostic on purpose: shared pages/components
    // invalidate `["control-session", id, "status"]` after uploads, and that
    // must keep hitting this entry for work step sessions too. Session ids
    // are UUIDs from disjoint tables, so the same id can never denote both
    // kinds. Only the fetch path differs per kind.
    key: ["control-session", controlSessionId ?? "", "status"],
    fetcher: async () => {
      const path =
        kind === "work_step"
          ? `/work-steps/work-step-session/${controlSessionId}/status`
          : `/controls/v2/control-session/${controlSessionId}/status`;
      const response = await fetchFastApi({
        method: "GET",
        path,
      });
      return parseControlSessionStatusResponse(response);
    },
    enabled: !!controlSessionId,
  });

  const { data, refetch } = query;

  // 1) Poll while any procedure session has at least one still-active field
  //    (pending/processing/retrying). The degraded "unknown" sentinel counts
  //    as settled so an unrecognized backend value stops the poll instead of
  //    driving it forever.
  useEffect(() => {
    if (!data) return;
    const anyNotReady = data.procedureSessions.some(hasActiveLifecycleState);
    if (!anyNotReady) return;
    const id = setInterval(() => {
      void refetch();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [data, refetch]);

  // 2) Transition detection -> invalidate the narrow per-field sub-keys.
  //    Session-runs refresh is intentionally NOT chained here: this hook is
  //    called from pages outside the SessionRunsProvider (AuditSessionPageV2,
  //    AuditDocumentsPageV2). The work paper view, which DOES need session-
  //    runs to stay current, mounts useSessionRunsTransitions for that and
  //    useSessionRunsPolling to keep the session-runs context warm.
  useEffect(() => {
    const prev = previousRef.current;
    const curr = data;
    if (prev && curr) {
      let anyTestsReadyFlip = false;
      let anyDocumentsReadyFlip = false;
      for (const currPs of curr.procedureSessions) {
        const prevPs = prev.procedureSessions.find((p) => p.id === currPs.id);
        if (!prevPs) continue;
        const psId = currPs.id;
        if (
          prevPs.documentsState !== "ready" &&
          currPs.documentsState === "ready"
        ) {
          // Broad invalidation: cascades to `documents`, `source-rows`,
          // and the heavy `["procedure-session", psId]` hydration. The
          // last one is load-bearing — the sample-card doc-count badge
          // reads from `auditSession.sourceRows[i].matchItems` (see
          // AuditSampleList's `docCountByRowId` memo), which lives in
          // the heavy key, not in the source-rows slice.
          void invalidate(["procedure-session", psId]);
          anyDocumentsReadyFlip = true;
        }
        if (
          prevPs.sourceRowsState !== "ready" &&
          currPs.sourceRowsState === "ready"
        ) {
          void invalidate(["procedure-session", psId, "source-rows"]);
          void invalidate(["procedure-session", psId, "table-shell"]);
        }
        if (prevPs.testsState !== "ready" && currPs.testsState === "ready") {
          void invalidate(["procedure-session", psId, "source-rows"]);
          void invalidate(["procedure-session", psId, "table-shell"]);
          anyTestsReadyFlip = true;
        }
        if (
          prevPs.workPaperState !== "ready" &&
          currPs.workPaperState === "ready"
        ) {
          void invalidate(["procedure-session", psId, "table-shell"]);
        }
        if (prevPs.state !== "ready" && currPs.state === "ready") {
          void invalidate(["procedure-session", psId, "table-shell"]);
        }
      }
      // A tests-ready flip is when a run may have changed envelope-level
      // facts — most importantly `auditorTrailEnabled`/`auditorTrailPending`
      // (the trail is persisted during test execution, so the old↔new
      // walkthrough switch and the rerun-pending banner must react without a
      // page reload). Once per tick, not per procedure. Prefix matching means
      // this also refetches the status entry itself — a no-op transition-wise,
      // since every flip just compared equal-or-ready.
      if (anyTestsReadyFlip && controlSessionId) {
        void invalidate(["control-session", controlSessionId]);
      }
      // Document processing runs the analytics-input prefill, which writes
      // input bindings in the background alongside the `is_analytics_input`
      // flag. The flag refreshes through the procedure-session sweep above,
      // but bindings are keyed by the analytics owner, not by session, so no
      // session-scoped sweep reaches them. Once per tick because the entries
      // are per-owner, not per-procedure.
      if (anyDocumentsReadyFlip && analyticsOwnerKind && analyticsOwnerId) {
        void invalidate(
          analyticsKeyPrefix({
            kind: analyticsOwnerKind,
            id: analyticsOwnerId,
          }),
        );
      }
    }
    previousRef.current = curr;
  }, [
    data,
    invalidate,
    controlSessionId,
    analyticsOwnerKind,
    analyticsOwnerId,
  ]);

  return query;
}
